/**
 * <ScrollProgress /> — thin emerald progress bar at the top of the viewport
 * that fills as the visitor scrolls down the page.
 *
 * v34.1 (Mar 2026): adds a modern "you are here in the document" cue. Used
 * by Linear, Vercel, Stripe — small detail, big polish payoff.
 *
 * Implementation notes:
 *   - Uses requestAnimationFrame to throttle scroll handler.
 *   - Reads `document.documentElement.scrollTop / scrollHeight - innerHeight`
 *     to get 0..1 progress.
 *   - Respects prefers-reduced-motion: if reduced-motion is on the bar still
 *     renders, just without the elastic transition.
 */
import React, { useEffect, useRef, useState } from 'react';

const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const update = () => {
      const doc = document.documentElement;
      const scrollTop = doc.scrollTop || document.body.scrollTop;
      const max = doc.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(1, Math.max(0, scrollTop / max)) : 0);
      rafRef.current = null;
    };

    const onScroll = () => {
      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div
      data-testid="scroll-progress"
      aria-hidden
      className="fixed top-0 left-0 right-0 z-[100] h-[3px] pointer-events-none"
    >
      <div
        className="h-full bg-emerald-400 bg-gradient-to-r from-emerald-400 via-emerald-300 to-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.7)]"
        style={{
          width: `${progress * 100}%`,
          transition: 'width 80ms linear',
        }}
      />
    </div>
  );
};

export default ScrollProgress;
